import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import DictationInput from '../components/DictationInput';
import { Building, Save, Globe, Phone, Mail, MapPin, Clock } from 'lucide-react';

const CATEGORIES = [
  { value: 'food', label: 'Food Assistance' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'housing', label: 'Housing' },
  { value: 'recovery', label: 'Recovery Support' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'employment', label: 'Employment' },
  { value: 'legal', label: 'Legal Support' },
  { value: 'education', label: 'Education & Training' },
  { value: 'family', label: 'Family Services' },
  { value: 'other', label: 'Other Services' },
];

export default function OrganizationProfilePage() {
  const { user, axiosAuth } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: 'other',
    description: '',
    eligibility: '',
    service_area: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    hours: ''
  });
  const api = axiosAuth();

  useEffect(() => {
    api.get('/partner-organizations/mine').then(r => {
      if (r.data) {
        setOrg(r.data);
        setForm({
          name: r.data.name || '',
          category: r.data.category || 'other',
          description: r.data.description || '',
          eligibility: r.data.eligibility || '',
          service_area: r.data.service_area || '',
          address: r.data.address || '',
          phone: r.data.phone || '',
          email: r.data.email || '',
          website: r.data.website || '',
          hours: r.data.hours || ''
        });
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Organization name is required');
      return;
    }
    setSaving(true);
    try {
      if (org) {
        await api.put(`/partner-organizations/${org.id}`, form);
        toast.success('Organization profile updated!');
      } else {
        const res = await api.post('/partner-organizations', form);
        setOrg(res.data);
        toast.success('Organization profile created!');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="organization-profile-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Organization Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {org ? 'Update your organization information' : 'Set up your organization to connect with participants'}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Building className="w-4 h-4 text-emerald-600" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Organization Name *</Label>
                <Input 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Your organization name"
                  data-testid="org-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Service Category *</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="org-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <DictationInput 
                  value={form.description} 
                  onChange={(val) => setForm({...form, description: val})}
                  placeholder="Describe your organization and services... (speak or type)"
                  rows={4}
                  data-testid="org-description-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Eligibility Requirements</Label>
                <DictationInput 
                  value={form.eligibility} 
                  onChange={(val) => setForm({...form, eligibility: val})}
                  placeholder="Who can access your services? Any requirements? (speak or type)"
                  rows={2}
                  showCleanup={false}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Service Area</Label>
                <Input 
                  value={form.service_area} 
                  onChange={e => setForm({...form, service_area: e.target.value})}
                  placeholder="e.g., Greater Metro Area, Citywide, County-wide"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Address</Label>
                <Input 
                  value={form.address} 
                  onChange={e => setForm({...form, address: e.target.value})}
                  placeholder="Street address"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Phone size={14} /> Phone</Label>
                  <Input 
                    value={form.phone} 
                    onChange={e => setForm({...form, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Mail size={14} /> Email</Label>
                  <Input 
                    type="email"
                    value={form.email} 
                    onChange={e => setForm({...form, email: e.target.value})}
                    placeholder="contact@organization.org"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Globe size={14} /> Website</Label>
                  <Input 
                    value={form.website} 
                    onChange={e => setForm({...form, website: e.target.value})}
                    placeholder="https://yourwebsite.org"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-1.5"><Clock size={14} /> Hours</Label>
                  <Input 
                    value={form.hours} 
                    onChange={e => setForm({...form, hours: e.target.value})}
                    placeholder="Mon-Fri 9AM-5PM"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"
            data-testid="save-org-button"
          >
            <Save size={16} />
            {saving ? 'Saving...' : (org ? 'Update Profile' : 'Create Profile')}
          </Button>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <Card className="border-0 shadow-sm rounded-2xl sticky top-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Profile Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <Building className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold">{form.name || 'Organization Name'}</p>
                    <p className="text-xs text-muted-foreground capitalize">{form.category}</p>
                  </div>
                </div>
                {form.description && (
                  <p className="text-sm text-muted-foreground">{form.description}</p>
                )}
                <div className="text-xs space-y-1 text-muted-foreground">
                  {form.service_area && <p>Service Area: {form.service_area}</p>}
                  {form.phone && <p>Phone: {form.phone}</p>}
                  {form.email && <p>Email: {form.email}</p>}
                  {form.hours && <p>Hours: {form.hours}</p>}
                </div>
                {org?.verified && (
                  <div className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                    Verified Organization
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
